<template>
  <PVBreadcrumb
    class="bread-crumb"
    :style="{ marginTop: isFrame() ? '0px' : 'var(--nav-bar-height, 96px)' }"
    :model="breadcrumbs"
  >
    <template #item="{ item }">
      <a v-if="item && item.external" :href="item.url">
        <span v-if="item.icon" class="text-primary-500">
          <FontAwesomeIcon :icon="iconMap[item.icon]" />
        </span>
        <span v-else>{{ item.label }}</span>
      </a>
      <router-link
        v-else-if="
          item && indexOf(breadcrumbs, item) !== breadcrumbs.length - 1
        "
        :to="item.path ?? item.routeParams"
      >
        <span>{{ item.label }}</span>
      </router-link>
      <span v-else-if="item">{{ item.label }}</span>
    </template>
  </PVBreadcrumb>
</template>

<script lang="ts" setup>
  import { NAVIGATION_HISTORY_SESSION_STORAGE_KEY } from '@/interfaces';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';
  import { indexOf, last } from 'lodash';
  import { MenuItem } from 'primevue/menuitem';
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { RouteLocationNormalizedGeneric, useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import {
    BreadcrumbHierarchyItem,
    getBreadcrumbBranchForRoute,
    getBreadcrumbPath,
    HistoryItem,
  } from './breadCrumbHierarchy';
  import { isFrame } from '@/helpers';
  import { faHome, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
  import { prodisPlatformUrls } from '@/static/platform-urls';
  import { IVueexState } from '@/store';
  import { Env } from '@/interfaces/env';

  const { t } = useI18n();
  const store = useStore<IVueexState>();

  const breadcrumbs = ref<MenuItem[]>([]);
  const lastFromRoute = ref<RouteLocationNormalizedGeneric | null>(null);
  const lastToRoute = ref<RouteLocationNormalizedGeneric | null>(null);
  watch(
    () => store.state.language,
    () => {
      if (lastToRoute.value && lastFromRoute.value) {
        buildBreadcrumbs(lastFromRoute.value, lastToRoute.value);
      }
    },
  );
  const router = useRouter();
  router.afterEach(async (to, from) => {
    lastToRoute.value = to;
    lastFromRoute.value = from;
    await buildBreadcrumbs(from, to);
  });

  const iconMap: Record<string, IconDefinition> = {
    home: faHome,
  };

  async function buildBreadcrumbs(
    from: RouteLocationNormalizedGeneric,
    to: RouteLocationNormalizedGeneric,
  ): Promise<void> {
    const lastHistoryItem = last(getHistory());
    updateHistory(from, to);
    const breadcrumbBranch = getBreadcrumbBranchForRoute(to);
    const breadcrumbsBuffer = [];
    for (const item of getBreadcrumbPath(to.name as Route, breadcrumbBranch) ??
      []) {
      breadcrumbsBuffer.push(
        ...(await transformHierarchyItemToBreadcrumb(
          item,
          to,
          lastHistoryItem,
        )),
      );
    }
    breadcrumbs.value = breadcrumbsBuffer;

    if (isFrame()) {
      breadcrumbs.value.unshift({
        url: prodisPlatformUrls[store.state.env ?? Env.local],
        label: 'Daisy Platform',
        external: true,
        icon: 'home',
      });
    }
  }

  async function transformHierarchyItemToBreadcrumb(
    item: BreadcrumbHierarchyItem,
    to: HistoryItem,
    lastHistoryItem?: HistoryItem,
    isAdditional = false,
  ): Promise<MenuItem[]> {
    const customRouteLabel = await item.getLabel?.(to);
    const breadCrumb = {
      ...item,
      path: isAdditional
        ? undefined
        : getRouteFromHistory(item.route)?.fullPath,
      routeParams: item.getRouteParams?.(to),
      label: customRouteLabel ?? t(`breadCrumb.${item.route}`),
    };
    const additionalPrecedingBreadcrumb =
      item.getAdditionalPrecedingBreadcrumb?.(
        to,
        t,
        lastHistoryItem?.fullPath,
        router,
      );
    if (additionalPrecedingBreadcrumb) {
      const additionalBreadcrumb = await transformHierarchyItemToBreadcrumb(
        additionalPrecedingBreadcrumb,
        to,
        lastHistoryItem,
        true,
      );
      return [...additionalBreadcrumb, breadCrumb];
    } else return [breadCrumb];
  }

  function getHistory(): HistoryItem[] {
    const history = sessionStorage.getItem(
      NAVIGATION_HISTORY_SESSION_STORAGE_KEY,
    );
    return history ? JSON.parse(history) : [];
  }
  function setHistory(
    history: RouteLocationNormalizedGeneric[] | HistoryItem[],
  ): void {
    sessionStorage.setItem(
      NAVIGATION_HISTORY_SESSION_STORAGE_KEY,
      JSON.stringify(
        history.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { matched, ...historyItem } =
            item as RouteLocationNormalizedGeneric;
          return historyItem;
        }),
      ),
    );
  }

  function updateHistory(
    from: RouteLocationNormalizedGeneric,
    to: RouteLocationNormalizedGeneric,
  ): void {
    const history = getHistory();
    const previousRoute = last(history);

    /** 1. If same route, update last */
    if (previousRoute && isSameRoute(previousRoute, to)) {
      history[history.length - 1] = to;
      return setHistory(history);
    }

    /** 2. Get paths in hierarchy */
    const prefPathBranch = getBreadcrumbBranchForRoute(from);
    const toPathBranch = getBreadcrumbBranchForRoute(to);
    const toPath = getBreadcrumbPath(to.name as Route, toPathBranch);
    const prevPath = previousRoute
      ? getBreadcrumbPath(previousRoute.name as Route, prefPathBranch)
      : null;

    /** 3. If different roots, reset */
    if (
      !toPath ||
      !prevPath ||
      toPath[0].route !== prevPath[0].route // different root
    ) {
      return setHistory([to]);
    }

    /** 4. Find the deepest common ancestor */
    let commonDepth = 0;
    while (
      commonDepth < toPath.length &&
      commonDepth < prevPath.length &&
      toPath[commonDepth].route === prevPath[commonDepth].route
    ) {
      commonDepth++;
    }

    /** 5. If navigating "up", truncate */
    const truncatedHistory = history.slice(0, commonDepth);

    /** 6. Push the current route if it's not already the last one */
    const lastTruncated = last(truncatedHistory);
    if (!lastTruncated || !isSameRoute(lastTruncated, to)) {
      truncatedHistory.push(to);
    }

    setHistory(truncatedHistory);
  }

  function getRouteFromHistory(targetRoute: Route): HistoryItem | undefined {
    const history = getHistory();
    return history.find((route) => route.name === targetRoute);
  }

  function isSameRoute(route: HistoryItem, targetRoute: HistoryItem): boolean {
    const routeParamsConfig =
      routeParams[route.name as keyof typeof routeParams];
    if (!routeParamsConfig || !(PROJECT_TYPE_PARAM in routeParamsConfig)) {
      return route.name === targetRoute.name;
    }
    const projectTypeParam = routeParamsConfig[PROJECT_TYPE_PARAM];
    return (
      route.name === targetRoute.name &&
      route.params[projectTypeParam] === targetRoute.params[projectTypeParam] &&
      route.query[projectTypeParam] === targetRoute.query[projectTypeParam]
    );
  }
</script>

<style lang="scss" scoped>
  .bread-crumb {
    margin-left: auto;
    margin-right: auto;
    padding: 0;
    height: $bread-crumb-height;
    width: $main-content-width;
    display: flex;
    align-items: center;
    background-color: $app-background-color;

    a {
      color: $link-color;
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
</style>
